import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Plus, X } from 'shared/icons';
import PageHeader from '../components/layout/PageHeader';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import ConfirmModal from '../components/ui/ConfirmModal';
import ErrorCard from '../components/ui/ErrorCard';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import { Id, MONO_CLASS } from '../components/ui/Mono';
import Select from '../components/ui/Select';
import { SkeletonTable } from '../components/ui/Skeleton';
import StandardTable from '../components/ui/StandardTable';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import { FormatDate, FormatProximoAniversario, FormatTempoDeCasa } from '../utils/date';

// FUNCIONÁRIOS — entidade de SISTEMA (pessoas ≠ contas de login). Cargo,
// admissão e aniversário vivem aqui para servir a empresa inteira (WMS,
// controle de ponto futuro etc.); o WMS só VINCULA uma pessoa deste cadastro
// como operador (aba Operadores em /warehouse). "Excluir" = desativar
// (PATCH active — política de soft delete; sem DELETE).

const EMPTY_FORM = { name: '', code: '', job_title: '', hired_at: '', birth_date: '', user_id: '' };

function buildFormFromRow(row) {
    return {
        name: row?.name ?? '',
        code: row?.code ?? '',
        job_title: row?.job_title ?? '',
        // O wire devolve datetime ISO; o <input type="date"> exige AAAA-MM-DD.
        hired_at: String(row?.hired_at ?? '').slice(0, 10),
        birth_date: String(row?.birth_date ?? '').slice(0, 10),
        user_id: row?.user_id ?? '',
    };
}

function buildWirePayload(form) {
    return {
        name: form.name.trim(),
        code: form.code.trim(),
        job_title: form.job_title.trim() || null,
        hired_at: form.hired_at.trim() || null,
        birth_date: form.birth_date.trim() || null,
        user_id: form.user_id || null,
    };
}

const EmployeesPage = () => {
    const toast = useToast();
    const { checkPermission } = useAuth();
    const canManage = checkPermission('employees:manage');
    // O select de conta de login lista usuários — só quem pode lê-los o vê.
    const canReadUsers = checkPermission('users:read');

    const [rows, setRows] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [isModalOpen, setModalOpen] = useState(false);
    const [editingRow, setEditingRow] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [formError, setFormError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [rowToToggle, setRowToToggle] = useState(null);
    const [toggleLoading, setToggleLoading] = useState(false);

    const loadRows = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const data = await api.getEmployees({ limit: 1000 });
            setRows(Array.isArray(data?.results) ? data.results : []);
        } catch (error) {
            setLoadError(error?.message || 'Erro ao carregar');
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    useEffect(() => {
        if (!canReadUsers) {
            return;
        }
        api.getUsers()
            .then((data) => setUsers(Array.isArray(data?.users) ? data.users : []))
            .catch(() => setUsers([]));
    }, [canReadUsers]);

    const openCreate = () => {
        setEditingRow(null);
        setForm(EMPTY_FORM);
        setFormError(null);
        setModalOpen(true);
    };

    const openEdit = useCallback((row) => {
        setEditingRow(row);
        setForm(buildFormFromRow(row));
        setFormError(null);
        setModalOpen(true);
    }, []);

    const closeModal = () => {
        if (!saving) {
            setModalOpen(false);
        }
    };

    const setField = (name, value) => {
        setForm((previous) => ({ ...previous, [name]: value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!form.name.trim()) {
            setFormError({ field: 'name', message: 'Campo obrigatório' });
            return;
        }
        if (!form.code.trim()) {
            setFormError({ field: 'code', message: 'Campo obrigatório' });
            return;
        }
        // Cargo obrigatório (decisão do dono no aceite do M0).
        if (!form.job_title.trim()) {
            setFormError({ field: 'job_title', message: 'Campo obrigatório' });
            return;
        }

        const payload = buildWirePayload(form);
        setSaving(true);
        setFormError(null);
        try {
            if (editingRow) {
                await api.updateEmployee(editingRow.id, payload);
                toast.success('Funcionário atualizado');
            } else {
                await api.createEmployee(payload);
                toast.success('Funcionário criado');
            }
            setModalOpen(false);
            await loadRows();
        } catch (error) {
            if (error?.status === 409) {
                // ID de crachá já em uso por um funcionário ativo.
                setFormError({ field: 'code', message: error.message || 'Este ID já está em uso.' });
            } else {
                setFormError({ message: error?.message || 'Erro ao salvar' });
            }
        } finally {
            setSaving(false);
        }
    };

    const confirmToggle = async () => {
        if (!rowToToggle) {
            return;
        }
        const nextActive = !rowToToggle.active;
        setToggleLoading(true);
        try {
            await api.updateEmployee(rowToToggle.id, { active: nextActive });
            toast.success(`Funcionário ${nextActive ? 'ativado' : 'desativado'}`);
            await loadRows();
        } catch (error) {
            toast.error(error?.message || 'Erro ao salvar');
        } finally {
            setToggleLoading(false);
            setRowToToggle(null);
        }
    };

    const columns = useMemo(
        () => [
            {
                key: 'name',
                label: 'Nome',
                getValue: (row) => row.name,
                render: (row) => <span className="font-medium text-black dark:text-white">{row.name}</span>,
            },
            {
                key: 'code',
                label: 'ID do funcionário',
                getValue: (row) => row.code,
                render: (row) => <Id value={row.code} />,
            },
            {
                key: 'job_title',
                label: 'Cargo',
                getValue: (row) => row.job_title || '',
                render: (row) => row.job_title || <span className="text-mono-400">—</span>,
            },
            {
                // Decisão do dono (aceite do M0): a listagem mostra HÁ QUANTO
                // TEMPO trabalha, não a data — a data fica no tooltip e no modal.
                key: 'hired_at',
                label: 'Tempo de casa',
                getValue: (row) => row.hired_at || '',
                render: (row) => (
                    <span title={row.hired_at ? FormatDate(row.hired_at.slice(0, 10)) : undefined}>
                        {FormatTempoDeCasa(row.hired_at)}
                    </span>
                ),
            },
            {
                key: 'birth_date',
                label: 'Aniversário',
                getValue: (row) => row.birth_date || '',
                render: (row) => (
                    <span title={row.birth_date ? FormatDate(row.birth_date.slice(0, 10)) : undefined}>
                        {FormatProximoAniversario(row.birth_date)}
                    </span>
                ),
            },
            {
                key: 'active',
                label: 'Status',
                getValue: (row) => (row.active ? 1 : 0),
                render: (row) =>
                    row.active ? <Badge variant="success">Ativo</Badge> : <Badge variant="neutral">Inativo</Badge>,
            },
            {
                key: 'actions',
                label: 'Ações',
                sortable: false,
                searchable: false,
                exportable: false,
                getValue: () => '',
                render: (row) =>
                    canManage ? (
                        <div className="flex flex-wrap gap-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    openEdit(row);
                                }}
                            >
                                Editar
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setRowToToggle(row);
                                }}
                                className={
                                    row.active
                                        ? 'text-orange-600 hover:text-orange-700'
                                        : 'text-green-600 hover:text-green-700'
                                }
                                title={row.active ? 'Desativar' : 'Ativar'}
                            >
                                {row.active ? (
                                    <>
                                        <X size={14} className="mr-1" />
                                        Desativar
                                    </>
                                ) : (
                                    <>
                                        <Check size={14} className="mr-1" />
                                        Ativar
                                    </>
                                )}
                            </Button>
                        </div>
                    ) : (
                        <span className="text-mono-400">—</span>
                    ),
            },
        ],
        [canManage, openEdit],
    );

    return (
        <div className="space-y-6">
            <PageHeader
                title="Funcionários"
                subtitle="Cadastro de pessoas da empresa (≠ contas de login): cargo, admissão e aniversário. O WMS e futuras funcionalidades vinculam pessoas deste cadastro."
            />

            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-end">
                {canManage ? (
                    <Button onClick={openCreate} className="w-full sm:w-auto">
                        <Plus size={18} className="mr-2" />
                        Novo — Funcionário
                    </Button>
                ) : null}
            </div>

            {loading ? (
                <SkeletonTable columns={8} rows={6} />
            ) : loadError ? (
                <ErrorCard
                    title="Funcionários"
                    message={`Não foi possível carregar os dados.\n${loadError}`}
                    action="Tentar Novamente"
                    actionFn={loadRows}
                />
            ) : (
                <StandardTable
                    rows={rows}
                    columns={columns}
                    rowKey={(row, index) => row.id || index}
                    countLabel="funcionários"
                    exportFileName="funcionarios"
                    emptyMessage="Nenhum funcionário cadastrado."
                />
            )}

            <Modal
                isOpen={isModalOpen}
                onClose={closeModal}
                title={editingRow ? 'Editar — Funcionário' : 'Novo — Funcionário'}
                size="md"
            >
                <form onSubmit={handleSubmit} className="space-y-4">
                    <Input
                        label="Nome"
                        value={form.name}
                        onChange={(event) => setField('name', event.target.value)}
                        errorMessage={formError?.field === 'name' ? formError.message : undefined}
                        disabled={saving}
                        autoComplete="off"
                    />
                    <div>
                        <Input
                            label="ID do funcionário"
                            value={form.code}
                            onChange={(event) => setField('code', event.target.value)}
                            placeholder="ex.: 103"
                            inputMode="numeric"
                            errorMessage={formError?.field === 'code' ? formError.message : undefined}
                            disabled={saving}
                            autoComplete="off"
                            className={MONO_CLASS}
                        />
                        {formError?.field !== 'code' ? (
                            <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">
                                ID do crachá — único entre funcionários ativos; é ele que o coletor do WMS bipa.
                            </p>
                        ) : null}
                    </div>
                    <Input
                        label="Cargo"
                        value={form.job_title}
                        onChange={(event) => setField('job_title', event.target.value)}
                        placeholder="ex.: Conferente"
                        errorMessage={formError?.field === 'job_title' ? formError.message : undefined}
                        disabled={saving}
                        autoComplete="off"
                    />
                    <Input
                        label="Data de admissão"
                        type="date"
                        value={form.hired_at}
                        onChange={(event) => setField('hired_at', event.target.value)}
                        disabled={saving}
                    />
                    <Input
                        label="Data de nascimento"
                        type="date"
                        value={form.birth_date}
                        onChange={(event) => setField('birth_date', event.target.value)}
                        disabled={saving}
                    />
                    {canReadUsers ? (
                        <div>
                            <Select
                                label="Conta de login vinculada"
                                value={form.user_id}
                                onChange={(event) => setField('user_id', event.target.value)}
                                disabled={saving}
                            >
                                <option value="">Sem conta vinculada</option>
                                {users.map((user) => (
                                    <option key={user.id} value={user.id}>
                                        {user.email}
                                    </option>
                                ))}
                            </Select>
                            <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">
                                Opcional — nem todo funcionário tem acesso ao sistema.
                            </p>
                        </div>
                    ) : null}
                    {formError && !formError.field ? (
                        <p role="alert" className="text-sm text-danger-600 dark:text-danger-400">
                            {formError.message}
                        </p>
                    ) : null}

                    <div className="flex flex-col gap-3 pt-4 sm:flex-row">
                        <Button type="submit" loading={saving} className="flex-1">
                            Salvar
                        </Button>
                        <Button type="button" onClick={closeModal} variant="secondary" className="sm:w-auto">
                            Cancelar
                        </Button>
                    </div>
                </form>
            </Modal>

            <ConfirmModal
                isOpen={Boolean(rowToToggle)}
                onClose={() => setRowToToggle(null)}
                onConfirm={confirmToggle}
                title={rowToToggle?.active ? 'Desativar' : 'Ativar'}
                message={
                    rowToToggle
                        ? `Confirma ${rowToToggle.active ? 'desativar' : 'ativar'} "${rowToToggle.name}"?${
                              rowToToggle.active
                                  ? ' Um funcionário inativo não se identifica no coletor do WMS.'
                                  : ''
                          }`
                        : ''
                }
                confirmText={rowToToggle?.active ? 'Desativar' : 'Ativar'}
                loading={toggleLoading}
            />
        </div>
    );
};

export default EmployeesPage;
